import React, { useEffect, useMemo, useState } from 'react';
import { usePhotos } from '../hooks/usePhotos';
import { Search, SlidersHorizontal, X, MapPin, Calendar, Images } from 'lucide-react';

const pageSize = 24;

/* ─── Lightbox ─── */
const Lightbox = ({ photo, onClose }) => {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative max-w-4xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <img
          src={photo.imageUrl}
          alt={photo.title}
          className="w-full max-h-[70vh] object-contain bg-slate-950"
        />
        <div className="p-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-semibold text-slate-900 text-sm leading-snug">{photo.title}</h3>
            <div className="flex items-center gap-3 mt-1.5">
              {photo.location && (
                <span className="flex items-center gap-1 text-xs text-slate-500">
                  <MapPin className="w-3 h-3" />{photo.location}
                </span>
              )}
              <span className="flex items-center gap-1 text-xs text-slate-400">
                <Calendar className="w-3 h-3" />{formatHumanDate(photo.uploadDate)}
              </span>
              {photo.source === 'fb' && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                  Yeni
                </span>
              )}
            </div>
            {photo.description && (
              <p className="mt-2 text-xs text-slate-500 line-clamp-2">{photo.description}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition"
          >
            <X className="w-4 h-4 text-slate-600" />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Ana Sayfa ─── */
const PhotosPage = () => {
  const { photos: all, loading } = usePhotos();

  const [q, setQ] = useState('');
  const [qDeb, setQDeb] = useState('');
  const [location, setLocation] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setQDeb(q.trim().toLocaleLowerCase('tr')), 300);
    return () => clearTimeout(t);
  }, [q]);

  const locationOptions = useMemo(() => {
    const set = new Set();
    all.forEach(p => { if (p.location) set.add(p.location); });
    return [...set].sort((a, b) => a.localeCompare(b, 'tr'));
  }, [all]);

  const filtered = useMemo(() => {
    let arr = all;
    if (location) arr = arr.filter(p => (p.location || '').toLocaleLowerCase('tr') === location.toLocaleLowerCase('tr'));
    if (qDeb) arr = arr.filter(p => `${p.title} ${p.location ?? ''}`.toLocaleLowerCase('tr').includes(qDeb));
    if (sortBy === 'newest')     return [...arr].sort((a, b) => b.uploadDate - a.uploadDate);
    if (sortBy === 'title-asc')  return [...arr].sort((a, b) => (a.title || '').localeCompare(b.title || '', 'tr'));
    if (sortBy === 'title-desc') return [...arr].sort((a, b) => (b.title || '').localeCompare(a.title || '', 'tr'));
    return arr;
  }, [all, location, qDeb, sortBy]);

  useEffect(() => setPage(1), [location, qDeb, sortBy]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page]);

  const clearAll = () => { setQ(''); setLocation(''); setSortBy('newest'); };

  const chips = [
    qDeb     && { k: 'Arama',   v: q,        onClear: () => setQ('') },
    location && { k: 'Konum',   v: location, onClear: () => setLocation('') },
    sortBy !== 'newest' && { k: 'Sıralama', v: sortBy === 'title-asc' ? 'A-Z' : 'Z-A', onClear: () => setSortBy('newest') },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-slate-50">
      {lightbox && <Lightbox photo={lightbox} onClose={() => setLightbox(null)} />}

      <div className="container mx-auto px-4 py-6 max-w-screen-xl">

        {/* Başlık */}
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Fotoğraf Arşivi</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            <span className="font-medium text-slate-700">{filtered.length.toLocaleString('tr')}</span> fotoğraf
          </p>
        </div>

        {/* Filtre barı */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-5">
          <div className="p-3 flex items-center gap-2">

            {/* Arama */}
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Başlık veya konum ara…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg
                  focus:outline-none focus:ring-2 focus:ring-slate-900 bg-slate-50 placeholder:text-slate-400"
              />
              {q && (
                <button onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Sıralama */}
            <div className="relative shrink-0 w-36">
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="w-full appearance-none pl-3 pr-7 py-2 text-sm bg-white border border-slate-200 rounded-lg
                  text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900 transition"
              >
                <option value="newest">En Yeni</option>
                <option value="title-asc">A → Z</option>
                <option value="title-desc">Z → A</option>
              </select>
              <svg className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            {/* Filtre toggle */}
            <button
              onClick={() => setFiltersOpen(o => !o)}
              className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition shrink-0 ${
                filtersOpen || chips.length > 0
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span className="hidden sm:inline">Filtrele</span>
              {chips.length > 0 && (
                <span className="text-xs rounded-full px-1.5 py-0.5 font-medium bg-white text-slate-900">
                  {chips.length}
                </span>
              )}
            </button>
          </div>

          {/* Konum filtresi */}
          {filtersOpen && (
            <div className="border-t border-slate-100 p-3">
              <div className="flex items-center gap-2">
                <div className="relative w-64">
                  <select
                    value={location}
                    onChange={e => setLocation(e.target.value)}
                    className="w-full appearance-none pl-3 pr-7 py-2 text-sm bg-white border border-slate-200 rounded-lg
                      text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900 transition"
                  >
                    <option value="">Konum (Tümü)</option>
                    {locationOptions.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                  <svg className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {chips.length > 0 && (
                  <button onClick={clearAll} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 ml-auto transition">
                    <X className="w-3 h-3" /> Tümünü sıfırla
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Chip'ler */}
          {chips.length > 0 && (
            <div className={`flex flex-wrap gap-1.5 px-3 pb-3 ${filtersOpen ? '' : 'border-t border-slate-100 pt-3'}`}>
              {chips.map((c, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-slate-100 text-slate-700 border border-slate-200">
                  <span className="font-medium">{c.k}:</span> {c.v}
                  <button onClick={c.onClear} className="ml-0.5 rounded-full p-0.5 hover:bg-slate-200 transition">
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Skeleton */}
        {loading && (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="rounded-xl overflow-hidden animate-pulse">
                <div className="h-48 bg-slate-200" />
                <div className="bg-white p-3 space-y-2">
                  <div className="h-3 bg-slate-100 rounded w-3/4" />
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Grid */}
        {!loading && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
              {paged.map(p => (
                <figure
                  key={p.id}
                  onClick={() => setLightbox(p)}
                  className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-slate-300 hover:shadow-md transition-all duration-150 cursor-pointer"
                >
                  {/* Görsel */}
                  <div className="relative overflow-hidden bg-slate-100 h-48">
                    <img
                      src={p.imageUrl}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={e => { e.currentTarget.parentElement.classList.add('broken'); e.currentTarget.style.opacity = '0'; }}
                    />
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                        <svg className="w-5 h-5 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </div>
                    </div>
                    {/* Yeni badge */}
                    {p.source === 'fb' && (
                      <span className="absolute top-2 left-2 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500 text-white font-medium shadow-sm">
                        Yeni
                      </span>
                    )}
                  </div>

                  {/* Caption */}
                  <figcaption className="p-3">
                    <p className="text-xs font-semibold text-slate-800 line-clamp-2 leading-snug">{p.title}</p>
                    <div className="flex items-center justify-between mt-1.5 gap-2">
                      <span className="flex items-center gap-1 text-[11px] text-slate-400 truncate">
                        <MapPin className="w-3 h-3 shrink-0" />
                        <span className="truncate">{p.location || '—'}</span>
                      </span>
                      <span className="text-[11px] text-slate-400 shrink-0">{formatHumanDate(p.uploadDate)}</span>
                    </div>
                  </figcaption>
                </figure>
              ))}

              {paged.length === 0 && (
                <div className="col-span-full flex flex-col items-center justify-center py-16 text-slate-400">
                  <Images className="w-10 h-10 mb-3 opacity-40" />
                  <p className="text-sm">Fotoğraf bulunamadı.</p>
                  <button onClick={clearAll} className="mt-3 text-xs text-slate-500 underline underline-offset-2 hover:text-slate-800">
                    Filtreleri temizle
                  </button>
                </div>
              )}
            </div>

            {/* Sayfalama */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center gap-1.5">
                <button onClick={() => setPage(x => Math.max(1, x - 1))} disabled={page === 1}
                  className="px-3 py-2 text-sm rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition">
                  ← Önceki
                </button>
                {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                  let n;
                  if (totalPages <= 7) n = i + 1;
                  else if (page <= 4) n = i + 1;
                  else if (page >= totalPages - 3) n = totalPages - 6 + i;
                  else n = page - 3 + i;
                  return (
                    <button key={n} onClick={() => setPage(n)}
                      className={`w-9 h-9 text-sm rounded-lg border transition ${
                        page === n ? 'bg-slate-900 text-white border-slate-900 font-medium' : 'border-slate-200 hover:bg-slate-50 text-slate-600'
                      }`}>
                      {n}
                    </button>
                  );
                })}
                <button onClick={() => setPage(x => Math.min(totalPages, x + 1))} disabled={page === totalPages}
                  className="px-3 py-2 text-sm rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition">
                  Sonraki →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

function formatHumanDate(d) {
  const date = d instanceof Date ? d : new Date(d);
  const diff = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);
  if (diff > 365) return `${date.getFullYear()}`;
  if (diff > 30)  return `${Math.floor(diff / 30)} ay önce`;
  if (diff > 1)   return `${Math.floor(diff)} gün önce`;
  return 'Bugün';
}

export default PhotosPage;